/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
    names: {
        signUpSignIn: 'B2C_1_signIn_signUp',
        forgotPassword: 'B2C_1_reset',
        editProfile: 'B2C_1_edit_profile',
    },
    authorities: {
        signUpSignIn: {
            authority: 'https://devalive.b2clogin.com/devalive.onmicrosoft.com/B2C_1_signIn',
        },
        forgotPassword: {
            authority: 'https://devalive.b2clogin.com/devalive.onmicrosoft.com/B2C_1_reset',
        },
        editProfile: {
            authority: 'https://devalive.b2clogin.com/devalive.onmicrosoft.com/B2C_1_edit_profile',
        },
    },
    authorityDomain: 'devalive.b2clogin.com',
};


/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const msalConfig = {
    auth: {
        clientId: '72152589-12ec-48e1-9f89-bbea04cf9433', // This is the ONLY mandatory field that you need to supply.
        authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        redirectUri: 'http://localhost:3000/', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const scopes = {
    read: ['https://devalive.onmicrosoft.com/0c41a565-25cd-4494-982f-0ef07d82575b/ToDoList.Read'],
    write: ['https://devalive.onmicrosoft.com/0c41a565-25cd-4494-982f-0ef07d82575b/ToDoList.ReadWrite'],
    callapi: ['https://devalive.onmicrosoft.com/0da89da3-033d-473e-ac47-8618b91309aa/FunctionsCall'],
};

export const protectedResources = {
    getUsers: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/getUsers'
    },
    updateUser: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/updateUser'
    },
    createUser: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/createUser'
    },
    deleteUser: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/deleteUser'
    },
    getUserPhoto: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/getUserPhoto'
    },
    updateUserPhoto: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/updateUserPhoto'
    },
    getSASToken: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/getSASToken'
    },
    sample: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/cosmosDbSample'
    },
    getCompanies: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/getCompanies'
    },
    updateCompany: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/updateCompany'
    },
    createCompany: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/createCompany'
    },
    getCompany: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/getCompany'
    },
    deleteCompany: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/deleteCompany'
    },
    getCompanyEntries: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/getCompanyEntries'
    },
    createBulkUsers: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/createBulkUsers'
    },
    getMasterdatas: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/getMasterdatas'
    },
    getMasterData: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/getMasterData'
    },
    updateMasterData: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/updateMasterData'
    },
    createMasterData: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/createMasterData'
    },
    deleteMasterData: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/deleteMasterData'
    },
    getUserEntries: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/getUserEntries'
    },
    getEntry: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/entries'
    },
    getCompanySeasonEntries: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/getCompanySeasonEntries'
    },
    getActiveSeason: {
        endpoint: 'https://alive-platform-backend.azurewebsites.net/api/getActiveSeason'
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [...scopes.read, ...scopes.write],
};
